// Búsqueda paginada de producto/pack/tienda filtrada por team (cliente) para reglas y condiciones
import { createHttpClient } from '@adapters/HttpClient';
import { PackApiAdapter } from '@modules/Packs/adapters/PackApiAdapter';
import { ShopsApiAdapter } from '@modules/Shops/List/adapters/ShopsApiAdapter';
import { ProductApiAdapter } from '@shared/adapters/ProductApiAdapter';
import { apiClientWH } from '@shared/services/ApiClient';
import { ProductService } from '@shared/services/ProductService';
import type { DynamicComboboxOption } from '@shared/components/ui/DynamicCombobox';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface ClientRuleConditionValueSelect {
  searchFn: (query: string, append?: boolean) => Promise<DynamicComboboxOption[]>;
  hasMore: boolean;
  resolveSelected: (value: string) => Promise<DynamicComboboxOption | null>;
}

/**
 * Selectores con scroll y búsqueda server-side para condiciones product_id / shop_id
 * cuando la regla pertenece a un cliente (tenant_external) concreto.
 */
export function useClientRuleConditionValueSelect(
  teamId: number | undefined,
  field: string,
): ClientRuleConditionValueSelect | null {
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const productService = useMemo(() => new ProductService(new ProductApiAdapter(httpClient)), [httpClient]);
  const packAdapter = useMemo(() => new PackApiAdapter(httpClient), [httpClient]);
  const shopsAdapter = useMemo(() => new ShopsApiAdapter(httpClient), [httpClient]);

  const productPageRef = useRef(1);
  const packPageRef = useRef(1);
  const shopPageRef = useRef(1);
  const [productHasMore, setProductHasMore] = useState(false);
  const [packHasMore, setPackHasMore] = useState(false);
  const [shopHasMore, setShopHasMore] = useState(false);

  useEffect(() => {
    productPageRef.current = 1;
    packPageRef.current = 1;
    shopPageRef.current = 1;
    setProductHasMore(false);
    setPackHasMore(false);
    setShopHasMore(false);
  }, [field, teamId]);

  const searchProducts = useCallback(
    async (query: string, append?: boolean) => {
      if (!teamId) return [];
      if (!append) productPageRef.current = 1;
      const page = productPageRef.current;
      const res = await productService.getProductsPaginated({
        searchTerm: query,
        page,
        limit: 20,
        tenantChildId: teamId,
        fields: 'basic',
      });
      setProductHasMore(res.hasMore);
      if (res.hasMore) productPageRef.current = page + 1;
      return res.products.map((p) => ({
        label: p.name ?? String(p.id),
        value: String(p.id),
      }));
    },
    [teamId, productService],
  );

  const searchPacks = useCallback(
    async (query: string, append?: boolean) => {
      if (!teamId) return [];
      if (!append) packPageRef.current = 1;
      const page = packPageRef.current;
      const res = await packAdapter.getPacksPaginated({
        searchTerm: query,
        page,
        limit: 20,
        teamId,
      });
      setPackHasMore(res.hasMore);
      if (res.hasMore) packPageRef.current = page + 1;
      return res.packs.map((pack) => ({
        label: pack.title ?? String(pack.id),
        value: String(pack.id),
      }));
    },
    [teamId, packAdapter],
  );

  const searchShops = useCallback(
    async (query: string, append?: boolean) => {
      if (!teamId) return [];
      if (!append) shopPageRef.current = 1;
      const page = shopPageRef.current;
      const res = await shopsAdapter.getShopsPaginated({
        searchTerm: query,
        page,
        limit: 20,
        team_id: teamId,
      });
      setShopHasMore(res.hasMore);
      if (res.hasMore) shopPageRef.current = page + 1;
      return res.shops.map((s) => ({
        label: s.name,
        value: String(s.id),
      }));
    },
    [teamId, shopsAdapter],
  );

  const resolveProduct = useCallback(
    async (value: string): Promise<DynamicComboboxOption | null> => {
      if (!teamId) return null;
      try {
        const p = await productService.getProductById(Number(value));
        return { label: p.name ?? value, value: String(p.id) };
      } catch {
        return null;
      }
    },
    [teamId, productService],
  );

  const resolvePack = useCallback(
    async (value: string): Promise<DynamicComboboxOption | null> => {
      if (!teamId) return null;
      try {
        const pack = await packAdapter.getPackById(Number(value));
        return { label: pack.title ?? value, value: String(pack.id) };
      } catch {
        return null;
      }
    },
    [teamId, packAdapter],
  );

  const resolveShop = useCallback(
    async (value: string): Promise<DynamicComboboxOption | null> => {
      if (!teamId) return null;
      const shop = await shopsAdapter.getShopById(Number(value));
      if (!shop) return null;
      return { label: shop.name, value: String(shop.id) };
    },
    [teamId, shopsAdapter],
  );

  if (!teamId || (field !== 'product_id' && field !== 'pack_id' && field !== 'shop_id')) {
    return null;
  }

  if (field === 'product_id') {
    return {
      searchFn: searchProducts,
      hasMore: productHasMore,
      resolveSelected: resolveProduct,
    };
  }

  if (field === 'pack_id') {
    return {
      searchFn: searchPacks,
      hasMore: packHasMore,
      resolveSelected: resolvePack,
    };
  }

  return {
    searchFn: searchShops,
    hasMore: shopHasMore,
    resolveSelected: resolveShop,
  };
}
